import type { Finish, Wrap } from '../shared/schemas';
import { invert, PHONE_FOCAL, squareToQuad, type QuadPoints } from './homography';
import type { LightingMaps } from './lighting';
import { reflectionMap } from './reflection';
import type { WatermarkRect } from './watermark';
import fragSrc from './shaders/floor.frag?raw';
import vertSrc from './shaders/floor.vert?raw';

export const GLOSS: Record<Finish, number> = { 'high-gloss': 0.4, gloss: 0.3, satin: 0.12, matte: 0 };
/** How much of a clear coat's mirror reflection each finish keeps. */
export const REFLECT: Record<Finish, number> = { 'high-gloss': 1.2, gloss: 1, satin: 0.35, matte: 0 };
/** How fast reflections blur with distance from the object (smoother coats stay sharper). */
export const REFLECT_BLUR: Record<Finish, number> = { 'high-gloss': 0.018, gloss: 0.05, satin: 0.1, matte: 0.1 };

/** Largest long edge of the rendered preview (WebGL2 guarantees 2048). */
export const RENDER_LONG_EDGE = 2048;

export interface RenderParams {
  /** Floor quad corners normalized to 0..1 (TL, TR, BR, BL). */
  quad: QuadPoints;
  repeats: [number, number];
  /** Inches of floor one texture repeat covers (tileInches x pattern size). */
  tileIn: number;
  finish: Finish;
  wrap?: Wrap;
  showOriginal?: boolean;
}

/**
 * repeats = feet * 12 / (tileInches * patternScale). The quad's far edge spans
 * the space type's width; its depth comes from the quad's real shape
 * (aspect = depth / width, see quadAspect) so chips stay square on the floor.
 */
export function computeRepeats(widthFt: number, aspect: number, tileInches: number, patternScale: number): [number, number] {
  const across = (widthFt * 12) / (tileInches * patternScale);
  return [across, across * aspect];
}

export function webgl2Supported(): boolean {
  try {
    return !!document.createElement('canvas').getContext('webgl2');
  } catch {
    return false;
  }
}

function compile(gl: WebGL2RenderingContext, type: number, src: string) {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(`Shader compile failed: ${gl.getShaderInfoLog(s)}`);
  return s;
}

/** Draws the coated floor over the photo. One instance per editor canvas. */
export class FloorRenderer {
  readonly canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private uniforms: Record<string, WebGLUniformLocation | null> = {};
  private photoTex: WebGLTexture;
  private maskTex: WebGLTexture;
  private lightTex: WebGLTexture;
  private reflectTex: WebGLTexture;
  private stemTex: WebGLTexture;
  private stemOn = false;
  private wmTex: WebGLTexture;
  private wm: { rect: WatermarkRect; opacity: number } | null = null;
  private floorTex: WebGLTexture | null = null;
  private textures = new Map<string, Promise<WebGLTexture>>();
  private maskSize = [1, 1];
  private photoSize = [1, 1];
  private lref = 0.18;
  private params: RenderParams | null = null;
  private anisotropy: { ext: EXT_texture_filter_anisotropic; max: number } | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', { preserveDrawingBuffer: true, antialias: false, alpha: false, premultipliedAlpha: false });
    if (!gl) throw new Error('WebGL2 not available');
    this.gl = gl;

    const program = gl.createProgram()!;
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, vertSrc));
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, fragSrc));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(`Program link failed: ${gl.getProgramInfoLog(program)}`);
    this.program = program;
    gl.useProgram(program);
    for (const name of ['u_photo', 'u_mask', 'u_floor', 'u_light', 'u_inv', 'u_repeats', 'u_maskTexel', 'u_lref', 'u_gloss', 'u_showOriginal', 'u_stochastic', 'u_reflect', 'u_reflectStrength', 'u_grazeScale', 'u_photoPx', 'u_reflectBlur', 'u_stem', 'u_stemHeight', 'u_tileIn', 'u_wm', 'u_wmRect', 'u_wmOpacity']) {
      this.uniforms[name] = gl.getUniformLocation(program, name);
    }
    gl.uniform1i(this.uniforms.u_photo, 0);
    gl.uniform1i(this.uniforms.u_mask, 1);
    gl.uniform1i(this.uniforms.u_floor, 2);
    gl.uniform1i(this.uniforms.u_light, 3);
    gl.uniform1i(this.uniforms.u_reflect, 4);
    gl.uniform1i(this.uniforms.u_stem, 5);
    gl.uniform1i(this.uniforms.u_wm, 6);
    gl.bindVertexArray(gl.createVertexArray());

    const ext = gl.getExtension('EXT_texture_filter_anisotropic');
    if (ext) this.anisotropy = { ext, max: Math.min(16, gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT)) };

    // Mipmapped: reflections read blurred levels.
    this.photoTex = this.makeTexture(gl.LINEAR_MIPMAP_LINEAR, gl.CLAMP_TO_EDGE);
    this.maskTex = this.makeTexture(gl.LINEAR, gl.CLAMP_TO_EDGE);
    this.lightTex = this.makeTexture(gl.LINEAR, gl.CLAMP_TO_EDGE);
    this.reflectTex = this.makeTexture(gl.NEAREST, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    this.stemTex = this.makeTexture(gl.NEAREST, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    this.setStem(null);
    this.wmTex = this.makeTexture(gl.LINEAR_MIPMAP_LINEAR, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
  }

  private makeTexture(filter: number, wrap: number) {
    const gl = this.gl;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap);
    return t;
  }

  /** Sets the working photo; the canvas takes its pixel size. */
  setPhoto(photo: HTMLCanvasElement | ImageBitmap) {
    const gl = this.gl;
    this.photoSize = [photo.width, photo.height];
    this.setRenderSize(photo.width, photo.height);
    gl.bindTexture(gl.TEXTURE_2D, this.photoTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, photo);
    gl.generateMipmap(gl.TEXTURE_2D);
  }

  /**
   * Output size: the given long edge (usually displayed size x device pixel
   * ratio), never below the photo itself and at most 2048. The coating is
   * drawn fresh per pixel, so the floor gets full detail even when the photo
   * has little (the rest of the photo is simply upscaled). Returns true if
   * the size changed (redraw needed).
   */
  fitTo(longEdge: number): boolean {
    const [pw, ph] = this.photoSize;
    const target = Math.min(RENDER_LONG_EDGE, Math.max(longEdge, Math.max(pw, ph)));
    const scale = target / Math.max(pw, ph);
    return this.setRenderSize(Math.round(pw * scale), Math.round(ph * scale));
  }

  private setRenderSize(w: number, h: number): boolean {
    if (this.canvas.width === w && this.canvas.height === h) return false;
    this.canvas.width = w;
    this.canvas.height = h;
    this.gl.viewport(0, 0, w, h);
    return true;
  }

  /** Mask values 0..255, row-major, any size (usually working-image size). */
  setMask(mask: Uint8Array, width: number, height: number) {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.maskTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    if (this.maskSize[0] === width && this.maskSize[1] === height) {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, width, height, gl.RED, gl.UNSIGNED_BYTE, mask);
    } else {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, mask);
      this.maskSize = [width, height];
    }
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.bindTexture(gl.TEXTURE_2D, this.reflectTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, width, height, 0, gl.RED, gl.FLOAT, reflectionMap(mask, width, height));
  }

  /**
   * Stem wall: per photo column [contact row 0..1 or -1, pixels per inch,
   * height in inches, unused] (stemWall.ts stemProfile). null turns it off.
   */
  setStem(profile: Float32Array | null) {
    const gl = this.gl;
    const data = profile ?? new Float32Array([-1, -1, -1, -1]);
    gl.bindTexture(gl.TEXTURE_2D, this.stemTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, data.length / 4, 1, 0, gl.RGBA, gl.FLOAT, data);
    this.stemOn = !!profile;
  }

  /**
   * Watermark image, drawn into the preview itself so screenshots and saved
   * images carry it. Decode it with premultiplied alpha.
   */
  setWatermarkImage(image: ImageBitmap) {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.wmTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.generateMipmap(gl.TEXTURE_2D);
  }

  /** Where the watermark goes and how strong it is; null hides it. */
  setWatermarkPlacement(placement: { rect: WatermarkRect; opacity: number } | null) {
    this.wm = placement;
  }

  setLighting(maps: LightingMaps) {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.lightTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG16F, maps.width, maps.height, 0, gl.RG, gl.FLOAT, maps.data);
    this.lref = maps.lref;
  }

  /** Loads (once) and caches a coating texture. */
  loadTexture(url: string, wrap: Wrap = 'mirror'): Promise<WebGLTexture> {
    const key = `${wrap}:${url}`;
    let p = this.textures.get(key);
    if (!p) {
      p = (async () => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Texture ${url}: ${res.status}`);
        const bmp = await createImageBitmap(await res.blob(), { colorSpaceConversion: 'none', premultiplyAlpha: 'none' });
        const gl = this.gl;
        const t = this.makeTexture(gl.LINEAR_MIPMAP_LINEAR, wrap === 'mirror' ? gl.MIRRORED_REPEAT : gl.REPEAT);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.SRGB8_ALPHA8, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
        gl.generateMipmap(gl.TEXTURE_2D);
        if (this.anisotropy) gl.texParameterf(gl.TEXTURE_2D, this.anisotropy.ext.TEXTURE_MAX_ANISOTROPY_EXT, this.anisotropy.max);
        bmp.close();
        return t;
      })();
      p.catch(() => this.textures.delete(key));
      this.textures.set(key, p);
    }
    return p;
  }

  /** Use a texture from loadTexture(). Synchronous so a slow load cannot overwrite a newer pick. */
  setTexture(tex: WebGLTexture) {
    this.floorTex = tex;
  }

  setParams(params: RenderParams) {
    this.params = params;
  }

  draw() {
    const gl = this.gl;
    const p = this.params;
    if (!p || !this.floorTex) return;
    let inv;
    try {
      inv = invert(squareToQuad(p.quad));
    } catch {
      return;
    }
    gl.useProgram(this.program);
    gl.uniformMatrix3fv(this.uniforms.u_inv, true, inv);
    gl.uniform2f(this.uniforms.u_repeats, p.repeats[0], p.repeats[1]);
    gl.uniform2f(this.uniforms.u_maskTexel, 1 / this.maskSize[0], 1 / this.maskSize[1]);
    gl.uniform1f(this.uniforms.u_lref, this.lref);
    gl.uniform1f(this.uniforms.u_gloss, GLOSS[p.finish]);
    gl.uniform1f(this.uniforms.u_showOriginal, p.showOriginal ? 1 : 0);
    gl.uniform1f(this.uniforms.u_stochastic, p.wrap === 'stochastic' ? 1 : 0);
    gl.uniform1f(this.uniforms.u_reflectStrength, REFLECT[p.finish]);
    gl.uniform1f(this.uniforms.u_reflectBlur, REFLECT_BLUR[p.finish]);
    // p.z of the inverse homography is 0 on the floor's horizon and grows
    // linearly below it; divided by its pixel gradient and the focal length it
    // gives tan(viewing angle above the floor), for the Fresnel term.
    const [pw, ph] = this.photoSize;
    const grad = Math.max(1e-12, Math.hypot(inv[6] / pw, inv[7] / ph));
    gl.uniform1f(this.uniforms.u_grazeScale, 1 / (grad * PHONE_FOCAL * Math.max(pw, ph)));
    gl.uniform2f(this.uniforms.u_photoPx, pw, ph);
    gl.uniform1f(this.uniforms.u_stemHeight, this.stemOn ? 1 : 0);
    gl.uniform1f(this.uniforms.u_tileIn, p.tileIn);
    const [wx, wy, ww, wh] = this.wm?.rect ?? [0, 0, 1, 1];
    gl.uniform4f(this.uniforms.u_wmRect, wx, wy, ww, wh);
    gl.uniform1f(this.uniforms.u_wmOpacity, this.wm?.opacity ?? 0);
    const units: [number, WebGLTexture][] = [[0, this.photoTex], [1, this.maskTex], [2, this.floorTex], [3, this.lightTex], [4, this.reflectTex], [5, this.stemTex], [6, this.wmTex]];
    for (const [unit, tex] of units) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, tex);
    }
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.activeTexture(gl.TEXTURE0);
  }

  destroy() {
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
